import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ShopperAiTagId,
} from "../../../ids/definitions";
import { MongoCustomerId, MongoUserId } from "../../../ids/mongo-ids";
import { NullableDateFromDb } from "../../../schema/helpers";
import { ShopperAiIntegrationType } from "../integration/integration";

export const CONVERSATION_STATUSES = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const;
export const ConversationStatus = Schema.Literal(...CONVERSATION_STATUSES);
export type ConversationStatus = typeof ConversationStatus.Type;

export const ConversationPriority = Schema.Literal(
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
);
export type ConversationPriority = typeof ConversationPriority.Type;

export const ConversationMode = Schema.Literal("DEMO", "LOCAL", "PROD");
export type ConversationMode = typeof ConversationMode.Type;

export const CONVERSATION_CLOSURE_REASONS = [
  "EXPIRED",
  "RESOLVED",
  "MANUAL",
  "SPAM",
  "DUPLICATE",
] as const;
export const ConversationClosureReason = Schema.Literal(
  ...CONVERSATION_CLOSURE_REASONS,
);
export type ConversationClosureReason = typeof ConversationClosureReason.Type;

export const CustomerSentiment = Schema.Literal(
  "POSITIVE",
  "NEUTRAL",
  "NEGATIVE",
);
export type CustomerSentiment = typeof CustomerSentiment.Type;

export const AssigneeSummary = Schema.Literal(
  "AI_AGENT_HANDLED",
  "MERCHANT_HANDLED",
  "AI_AGENT_HANDOFF_TO_HUMAN",
  "AI_AGENT_HANDOFF_TO_IGNORED",
  "MERCHANT_IGNORED",
);
export type AssigneeSummary = typeof AssigneeSummary.Type;

export const ConversationResponse = Schema.Struct({
  id: ConversationId,
  accountId: AccountId,
  status: ConversationStatus,
  mode: ConversationMode,
  priority: Schema.optional(ConversationPriority),
  mongoCustomerId: MongoCustomerId,
  mongoAssigneeId: Schema.NullOr(MongoUserId),
});

export type ConversationResponse = typeof ConversationResponse.Type;

export const ConversationListItemResponse = Schema.Struct({
  id: ConversationId,
  accountId: AccountId,
  status: ConversationStatus,
  priority: Schema.NullOr(ConversationPriority),
  mongoCustomerId: MongoCustomerId,
  mongoAssigneeId: Schema.NullOr(MongoUserId),
  readAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  summary: Schema.NullOr(Schema.String),
  customerSentiment: Schema.NullOr(CustomerSentiment),
  assigneeSummary: Schema.NullOr(AssigneeSummary),
  channels: Schema.Array(ShopperAiIntegrationType),
});
export type ConversationListItemResponse =
  typeof ConversationListItemResponse.Type;

export const ConversationListCursor = Schema.Struct({
  lastCustomerMessageAt: Schema.NullOr(Schema.DateFromString),
  id: ConversationId,
});
export type ConversationListCursor = typeof ConversationListCursor.Type;

export const UserTicketStats = Schema.Struct({
  userId: MongoUserId.fromDbBuffer,
  openTicketCount: Schema.NumberFromString.pipe(
    Schema.int(),
    Schema.nonNegative(),
  ),
  mostRecentTicketDate: NullableDateFromDb,
});
export type UserTicketStats = typeof UserTicketStats.Type;

export const UpdateConversationInput = Schema.Struct({
  status: Schema.optional(ConversationStatus),
  priority: Schema.optional(Schema.OptionFromNullOr(ConversationPriority)),
  readAt: Schema.optional(Schema.OptionFromNullOr(Schema.DateFromString)),
  mongoAssigneeId: Schema.optional(Schema.OptionFromNullOr(MongoUserId)),
  tagIds: Schema.optional(Schema.Array(ShopperAiTagId)),
});
export type UpdateConversationInput = typeof UpdateConversationInput.Type;
